import React, { useEffect, useState } from 'react';
import { api } from '../api/client';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Ноорог', pending: 'Хүлээгдэж буй', paid: 'Төлөгдсөн', ready: 'Бэлэн',
  partial: 'Хэсэгчилсэн', overdue: 'Хугацаа хэтэрсэн', cancelled: 'Цуцлагдсан',
  sent: 'Илгээсэн', approved: 'Батлагдсан', disputed: 'Маргаантай',
};
const STATUS_COLORS: Record<string, string> = {
  draft: '#6b7280', pending: '#f59e0b', paid: '#10b981', ready: '#3b82f6',
  partial: '#8b5cf6', overdue: '#ef4444', cancelled: '#9ca3af',
  sent: '#0ea5e9', approved: '#059669', disputed: '#dc2626',
};

type Tab = 'invoices' | 'emails' | 'bank';

export default function InvoicesPage() {
  const [tab, setTab] = useState<Tab>('invoices');

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid #e8e5dc', paddingBottom: 0 }}>
        {([
          { key: 'invoices' as Tab, label: '📋 Нэхэмжлэх' },
          { key: 'emails' as Tab, label: '✉️ Имэйл илгээлт' },
          { key: 'bank' as Tab, label: '🏦 Банкны мэдээлэл' },
        ]).map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '10px 20px', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 600,
            background: tab === t.key ? '#fff' : 'transparent',
            color: tab === t.key ? '#236844' : '#5f6f66',
            borderBottom: tab === t.key ? '3px solid #236844' : '3px solid transparent',
            borderRadius: '8px 8px 0 0', marginBottom: -2,
          }}>{t.label}</button>
        ))}
      </div>

      {tab === 'invoices' && <InvoicesTab />}
      {tab === 'emails' && <EmailsTab />}
      {tab === 'bank' && <BankInfoTab />}
    </div>
  );
}

/* ─── Invoices Tab ─── */
function InvoicesTab() {
  const [invoices, setInvoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all');
  const [payModal, setPayModal] = useState<any>(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('bank_transfer');
  const [payNotes, setPayNotes] = useState('');
  const [paying, setPaying] = useState(false);
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  // Гар нэхэмжлэх (нэг айлд тодорхой дүнгээр — торгууль, засвар, тест г.м.)
  const [manualModal, setManualModal] = useState(false);
  const [units, setUnits] = useState<any[]>([]);
  const [mUnitId, setMUnitId] = useState('');
  const [mAmount, setMAmount] = useState('');
  const [mDesc, setMDesc] = useState('Нэмэлт төлбөр');
  const [mPeriod, setMPeriod] = useState(period);
  const [mBusy, setMBusy] = useState(false);

  const loadInvoices = () => {
    setLoading(true);
    api.get(`/admin/invoices?period=${period}`).then(res => setInvoices(Array.isArray(res) ? res : res?.items || [])).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(loadInvoices, [period]);

  useEffect(() => {
    api.get('/admin/units?limit=1000')
      .then(res => {
        const list = (res?.items || res || []).slice().sort((a: any, b: any) => {
          const an = parseInt(String(a.unit_number).replace(/\D/g, '')) || 0;
          const bn = parseInt(String(b.unit_number).replace(/\D/g, '')) || 0;
          return String(a.building || '').localeCompare(String(b.building || '')) || an - bn;
        });
        setUnits(list);
      })
      .catch(() => {});
  }, []);

  const createManual = async () => {
    if (!mUnitId || !mAmount || parseInt(mAmount) <= 0) { alert('Айл болон 0-ээс их дүн оруулна уу'); return; }
    setMBusy(true);
    try {
      await api.post('/admin/invoices/manual', {
        unit_id: mUnitId,
        amount: parseInt(mAmount),
        description: mDesc || 'Нэмэлт төлбөр',
        period: mPeriod,
        approve: true,
      });
      alert('Нэхэмжлэх үүслээ');
      setManualModal(false);
      setMAmount('');
      if (mPeriod === period) loadInvoices(); else setPeriod(mPeriod);
    } catch (e: any) {
      alert('Алдаа: ' + (e?.body ? JSON.parse(e.body)?.detail || e.message : e.message));
    } finally { setMBusy(false); }
  };

  const generateInvoices = async () => {
    setGenerating(true);
    try {
      const res = await api.post('/admin/invoices/generate', { period });
      alert(`${res.message}`);
      loadInvoices();
    } catch (e: any) {
      alert('Алдаа: ' + e.message);
    } finally {
      setGenerating(false);
    }
  };

  const sendEmails = async () => {
    setSending(true);
    try {
      const res = await api.post('/admin/invoices/send-emails', { period });
      alert(`Илгээсэн: ${res.sent}, Амжилтгүй: ${res.failed}, Алгассан: ${res.skipped}`);
    } catch (e: any) {
      alert('Алдаа: ' + e.message);
    } finally {
      setSending(false);
    }
  };

  const approveInvoice = async (id: string) => {
    await api.post(`/admin/invoices/${id}/approve`);
    loadInvoices();
  };

  const recordPayment = async () => {
    if (!payModal || !payAmount) return;
    setPaying(true);
    try {
      await api.post(`/admin/payments/record?invoice_id=${payModal.id}&amount=${parseInt(payAmount)}&method=${payMethod}&notes=${encodeURIComponent(payNotes)}`);
      alert('Төлбөр бүртгэгдлээ');
      setPayModal(null);
      setPayAmount('');
      setPayNotes('');
      loadInvoices();
    } catch (e: any) {
      alert('Алдаа: ' + e.message);
    } finally {
      setPaying(false);
    }
  };

  const filtered = filter === 'all' ? invoices : invoices.filter(i => i.status === filter);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 style={{ fontWeight: 700, color: '#1c2b22', margin: 0 }}>Нэхэмжлэх</h2>
          <input type="month" value={period} onChange={e => setPeriod(e.target.value)} style={{
            padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14,
          }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={sendEmails} disabled={sending || invoices.length === 0} style={{
            background: '#0ea5e9', color: '#fff', border: 'none', padding: '8px 16px',
            borderRadius: 10, cursor: sending ? 'wait' : 'pointer', fontWeight: 600, fontSize: 13,
            opacity: (sending || invoices.length === 0) ? 0.5 : 1,
          }}>
            {sending ? 'Илгээж байна...' : '✉️ Имэйл илгээх'}
          </button>
          <button onClick={() => { setMPeriod(period); setManualModal(true); }} style={{
            background: '#fff', color: '#236844', border: '1.5px solid #236844', padding: '8px 16px',
            borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 13,
          }} title="Нэг айлд тодорхой дүнгээр нэхэмжлэх (торгууль, засвар, нэг удаагийн төлбөр)">
            🧾 Гар нэхэмжлэх
          </button>
          <button onClick={generateInvoices} disabled={generating} style={{
            background: 'linear-gradient(180deg, #2f7d52, #236844)', color: '#fff', border: 'none', padding: '8px 16px',
            borderRadius: 10, cursor: generating ? 'wait' : 'pointer', fontWeight: 600, fontSize: 13,
            opacity: generating ? 0.7 : 1,
          }}>
            {generating ? 'Үүсгэж байна...' : '➕ Нэхэмжлэх үүсгэх'}
          </button>
        </div>
      </div>

      {manualModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setManualModal(false)}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 460, maxWidth: '92%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 4px', fontWeight: 700, color: '#1c2b22' }}>Гар нэхэмжлэх</h3>
            <p style={{ margin: '0 0 16px', fontSize: 12.5, color: '#8a978f' }}>
              Нэг айлд тодорхой дүнгээр нэхэмжлэх үүсгэнэ. Тухайн сард нэхэмжлэх аль хэдийн байвал мөр нэмж дүнг өсгөнө —
              тусдаа тест нэхэмжлэх бол өөр хугацаа (сар) сонго.
            </p>
            <div style={{ display: 'grid', gap: 12 }}>
              <div>
                <label style={mLabel}>Орон сууц *</label>
                <select value={mUnitId} onChange={e => setMUnitId(e.target.value)} style={mInput}>
                  <option value="">-- Тоот сонгох --</option>
                  {units.map((u: any) => (
                    <option key={u.id} value={u.id}>
                      {[u.building, u.unit_number].filter(Boolean).join('-') || u.unit_number || u.id.slice(0, 8)}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={mLabel}>Дүн (₮) *</label>
                  <input type="number" value={mAmount} onChange={e => setMAmount(e.target.value)} style={mInput} placeholder="100" />
                </div>
                <div>
                  <label style={mLabel}>Хугацаа</label>
                  <input type="month" value={mPeriod} onChange={e => setMPeriod(e.target.value)} style={mInput} />
                </div>
              </div>
              <div>
                <label style={mLabel}>Утга</label>
                <input value={mDesc} onChange={e => setMDesc(e.target.value)} style={mInput} placeholder="Нэмэлт төлбөр" />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 18, justifyContent: 'flex-end' }}>
              <button onClick={() => setManualModal(false)} style={{ background: '#e8e5dc', color: '#2a3b32', border: 'none', padding: '8px 18px', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}>Болих</button>
              <button onClick={createManual} disabled={mBusy} style={{ background: 'linear-gradient(180deg, #2f7d52, #236844)', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: 10, cursor: mBusy ? 'wait' : 'pointer', fontWeight: 600, opacity: mBusy ? 0.7 : 1 }}>
                {mBusy ? 'Үүсгэж байна...' : 'Үүсгэх'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {['all', 'draft', 'ready', 'sent', 'partial', 'paid', 'overdue'].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            padding: '6px 14px', border: 'none', borderRadius: 6, cursor: 'pointer',
            background: filter === f ? '#236844' : '#e8e5dc', color: filter === f ? '#fff' : '#2a3b32',
            fontSize: 13, fontWeight: 500,
          }}>
            {f === 'all' ? 'Бүгд' : STATUS_LABELS[f] || f} {f !== 'all' && `(${invoices.filter(i => i.status === f).length})`}
          </button>
        ))}
      </div>

      {loading ? <p>Уншиж байна...</p> : (
        <div style={card}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e8e5dc', textAlign: 'left' }}>
                <th style={th}>Дугаар</th>
                <th style={th}>Орон сууц</th>
                <th style={th}>Хугацаа</th>
                <th style={th}>Өмнөх үлдэгдэл</th>
                <th style={th}>Нийт дүн</th>
                <th style={th}>Төлсөн</th>
                <th style={th}>Үлдэгдэл</th>
                <th style={th}>Статус</th>
                <th style={th}>Үйлдэл</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv: any) => (
                <tr key={inv.id} style={{ borderBottom: '1px solid #e8e5dc' }}>
                  <td style={td}><strong>{inv.invoice_number || '-'}</strong></td>
                  <td style={td}>{inv.unit_number || '-'}</td>
                  <td style={td}>{inv.period || '-'}</td>
                  <td style={td}>
                    {inv.previous_balance > 0 && (
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>₮{(inv.previous_balance ?? 0).toLocaleString()}</span>
                    )}
                  </td>
                  <td style={td}><strong>₮{(inv.total_amount ?? 0).toLocaleString()}</strong></td>
                  <td style={td}>
                    <span style={{ color: inv.paid_amount > 0 ? '#10b981' : '#9ca3af' }}>
                      ₮{(inv.paid_amount ?? 0).toLocaleString()}
                    </span>
                  </td>
                  <td style={td}>
                    {(inv.total_amount - inv.paid_amount) > 0 ? (
                      <span style={{ color: '#dc2626', fontWeight: 600 }}>₮{(inv.total_amount - inv.paid_amount).toLocaleString()}</span>
                    ) : inv.status === 'paid' ? (
                      <span style={{ color: '#10b981', fontWeight: 600 }}>0₮</span>
                    ) : '-'}
                  </td>
                  <td style={td}>
                    <span style={{
                      background: (STATUS_COLORS[inv.status] || '#6b7280') + '20',
                      color: STATUS_COLORS[inv.status] || '#6b7280',
                      padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                    }}>{STATUS_LABELS[inv.status] || inv.status}</span>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {inv.status === 'draft' && (
                        <button onClick={() => approveInvoice(inv.id)} style={actionBtn}>Батлах</button>
                      )}
                      {inv.status !== 'draft' && inv.status !== 'paid' && inv.status !== 'cancelled' && (
                        <button onClick={() => { setPayModal(inv); setPayAmount(String(inv.total_amount - inv.paid_amount)); }} style={{
                          background: '#3b82f6', color: '#fff', border: 'none',
                          padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12,
                        }}>Төлбөр</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Нэхэмжлэх олдсонгүй</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {payModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => setPayModal(null)}>
          <div style={{ ...card, width: 420, maxWidth: '90%' }} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 8px', fontWeight: 700, color: '#1c2b22' }}>Төлбөр бүртгэх</h3>
            <p style={{ fontSize: 13, color: '#5f6f66', marginBottom: 16 }}>
              {payModal.unit_number || '-'} · {payModal.period} · Нийт: ₮{(payModal.total_amount ?? 0).toLocaleString()} · Төлсөн: ₮{(payModal.paid_amount ?? 0).toLocaleString()}
            </p>
            <div style={{ marginBottom: 12 }}>
              <label style={label}>Дүн (₮)</label>
              <input type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)} style={input} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={label}>Төлбөрийн хэлбэр</label>
              <select value={payMethod} onChange={e => setPayMethod(e.target.value)} style={input}>
                <option value="bank_transfer">Банк шилжүүлэг</option>
                <option value="cash">Бэлнээр</option>
                <option value="qpay">QPay</option>
                <option value="card">Карт</option>
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={label}>Тэмдэглэл</label>
              <input value={payNotes} onChange={e => setPayNotes(e.target.value)} style={input} placeholder="Тэмдэглэл..." />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={recordPayment} disabled={paying} style={{
                background: '#3b82f6', color: '#fff', border: 'none', padding: '8px 20px',
                borderRadius: 10, cursor: paying ? 'wait' : 'pointer', fontWeight: 600, fontSize: 14,
                opacity: paying ? 0.7 : 1,
              }}>{paying ? 'Бүртгэж байна...' : 'Төлбөр бүртгэх'}</button>
              <button onClick={() => setPayModal(null)} style={{
                background: '#e8e5dc', color: '#2a3b32', border: 'none', padding: '8px 20px',
                borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14,
              }}>Болих</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Emails Tab ─── */
function EmailsTab() {
  const [emails, setEmails] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const load = async () => {
    setLoading(true);
    try {
      const [emailRes, statsRes] = await Promise.all([
        api.get(`/admin/invoice-emails?period=${period}`),
        api.get(`/admin/invoice-emails/stats?period=${period}`),
      ]);
      setEmails(emailRes?.items || []);
      setStats(statsRes);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, [period]);

  const EMAIL_STATUS: Record<string, { label: string; color: string }> = {
    sent: { label: 'Илгээсэн', color: '#10b981' },
    failed: { label: 'Амжилтгүй', color: '#ef4444' },
    pending: { label: 'Хүлээгдэж буй', color: '#f59e0b' },
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontWeight: 700, color: '#1c2b22', margin: 0 }}>Имэйл илгээлтүүд</h2>
        <input type="month" value={period} onChange={e => setPeriod(e.target.value)} style={{
          padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14,
        }} />
      </div>

      {stats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Нийт нэхэмжлэх', value: stats.total_invoices, color: '#3b82f6' },
            { label: 'Илгээсэн имэйл', value: stats.sent, color: '#10b981' },
            { label: 'Амжилтгүй', value: stats.failed, color: '#ef4444' },
          ].map(s => (
            <div key={s.label} style={{
              background: '#fff', borderRadius: 10, padding: '14px 20px', flex: 1,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderLeft: `4px solid ${s.color}`,
            }}>
              <div style={{ fontSize: 12, color: '#5f6f66', marginBottom: 4 }}>{s.label}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: '#1c2b22' }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {loading ? <p>Уншиж байна...</p> : (
        <div style={card}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #e8e5dc', textAlign: 'left' }}>
                <th style={th}>Хүлээн авагч</th>
                <th style={th}>Имэйл</th>
                <th style={th}>Хугацаа</th>
                <th style={th}>Дүн</th>
                <th style={th}>Статус</th>
                <th style={th}>Илгээсэн огноо</th>
              </tr>
            </thead>
            <tbody>
              {emails.map((e: any) => {
                const st = EMAIL_STATUS[e.status] || { label: e.status, color: '#6b7280' };
                return (
                  <tr key={e.id} style={{ borderBottom: '1px solid #e8e5dc' }}>
                    <td style={td}>{e.recipient_name || '-'}</td>
                    <td style={td}>{e.recipient_email}</td>
                    <td style={td}>{e.period}</td>
                    <td style={td}>₮{(e.total_amount ?? 0).toLocaleString()}</td>
                    <td style={td}>
                      <span style={{
                        background: st.color + '20', color: st.color,
                        padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                      }}>{st.label}</span>
                      {e.error_message && (
                        <span title={e.error_message} style={{ marginLeft: 4, cursor: 'help', fontSize: 12, color: '#ef4444' }}>⚠</span>
                      )}
                    </td>
                    <td style={td}>{e.sent_at ? new Date(e.sent_at).toLocaleString('mn-MN') : '-'}</td>
                  </tr>
                );
              })}
              {emails.length === 0 && (
                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>
                  Энэ сарын имэйл илгээлт байхгүй
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Bank Info Tab ─── */
function BankInfoTab() {
  const [bankName, setBankName] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get('/admin/settings/bank-info').then(res => {
      setBankName(res.bank_name || '');
      setAccountNumber(res.bank_account_number || '');
      setAccountName(res.bank_account_name || '');
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings/bank-info', {
        bank_name: bankName,
        bank_account_number: accountNumber,
        bank_account_name: accountName,
      });
      alert('Хадгалагдлаа!');
    } catch (e: any) {
      alert('Алдаа: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) return <p>Уншиж байна...</p>;

  return (
    <div>
      <h2 style={{ fontWeight: 700, color: '#1c2b22', marginBottom: 20 }}>Банкны данс</h2>
      <p style={{ color: '#5f6f66', marginBottom: 20, fontSize: 14 }}>
        Нэхэмжлэхийн имэйлд оршин суугчдад харуулах банкны дансны мэдээлэл.
      </p>
      <div style={{ ...card, maxWidth: 500 }}>
        <div style={{ marginBottom: 16 }}>
          <label style={label}>Банкны нэр</label>
          <input value={bankName} onChange={e => setBankName(e.target.value)} placeholder="Хаан банк" style={input} />
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={label}>Дансны дугаар</label>
          <input value={accountNumber} onChange={e => setAccountNumber(e.target.value)} placeholder="5012345678" style={input} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={label}>Дансны нэр (эзэмшигч)</label>
          <input value={accountName} onChange={e => setAccountName(e.target.value)} placeholder="Нарны Хороолол СӨХ" style={input} />
        </div>
        <button onClick={save} disabled={saving} style={{
          background: 'linear-gradient(180deg, #2f7d52, #236844)', color: '#fff', border: 'none',
          padding: '10px 24px', borderRadius: 10, cursor: saving ? 'wait' : 'pointer',
          fontWeight: 600, fontSize: 14, opacity: saving ? 0.7 : 1,
        }}>
          {saving ? 'Хадгалж байна...' : 'Хадгалах'}
        </button>
      </div>
    </div>
  );
}

const card: React.CSSProperties = {
  background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};
const th: React.CSSProperties = { padding: '10px 12px', fontSize: 13, color: '#5f6f66', fontWeight: 600 };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: 14, color: '#1c2b22' };
const mLabel: React.CSSProperties = { display: 'block', fontSize: 12.5, fontWeight: 600, color: '#5f6f66', marginBottom: 4 };
const mInput: React.CSSProperties = { width: '100%', padding: '8px 12px', border: '1.5px solid #dcdacf', borderRadius: 10, fontSize: 14, boxSizing: 'border-box' };
const actionBtn: React.CSSProperties = {
  background: '#10b981', color: '#fff', border: 'none',
  padding: '4px 12px', borderRadius: 4, cursor: 'pointer', fontSize: 13,
};
const label: React.CSSProperties = {
  display: 'block', fontSize: 13, fontWeight: 600, color: '#1c2b22', marginBottom: 6,
};
const input: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8,
  fontSize: 14, boxSizing: 'border-box',
};
